"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { Sidebar } from "./Sidebar";
import { chatHistoryService } from "@/services/chatHistoryService";
import { api } from "@/lib/api";
import type { Message, StoredChat, Language, ChatResponse } from "@/types";

// Silence detection settings
const SILENCE_THRESHOLD = 0.01;
const SILENCE_DURATION_MS = 2000;

export default function ChatInterface() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chats, setChats] = useState<StoredChat[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [language, setLanguage] = useState<Language>("ar");

  // Company selection state
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<number | null>(null);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(true);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs for voice recording
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const checkSilenceIntervalRef = useRef<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load chat history on mount
  useEffect(() => {
    setChats(chatHistoryService.getAllChats());
  }, []);

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await api.getCompanies();
        setCompanies(data.companies);
        setActiveCompanyId(data.active_company_id);
        if (data.multi_company && !data.active_company_id) {
          setShowCompanySelector(true);
        } else {
          setShowCompanySelector(false);
        }
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      } finally {
        setCompanyLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  // Handle company selection
  const handleSelectCompany = async (companyId: number) => {
    try {
      const result = await api.selectCompany(companyId);
      if (result.success && result.company) {
        setActiveCompanyId(result.company.id);
        setShowCompanySelector(false);
      }
    } catch (err) {
      console.error("Failed to select company:", err);
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Save messages to history when they change
  const chatIdRef = useRef<string | null>(null);
  const isSavingRef = useRef<boolean>(false);

  useEffect(() => {
    if (messages.length === 0) return;

    // Prevent concurrent saves
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    try {
      // Use ref to avoid double-save when chatId changes
      if (chatIdRef.current !== null) {
        chatHistoryService.updateChat(chatIdRef.current, messages);
      } else if (chatId) {
        chatHistoryService.updateChat(chatId, messages);
        chatIdRef.current = chatId;
      } else {
        const newChatId = chatHistoryService.saveChat(messages);
        setChatId(newChatId);
        chatIdRef.current = newChatId;
      }
      setChats(chatHistoryService.getAllChats());
    } finally {
      isSavingRef.current = false;
    }
  }, [messages, chatId]);

  // Sync chatIdRef when chatId changes externally (e.g., selecting a chat)
  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      if (checkSilenceIntervalRef.current) {
        clearInterval(checkSilenceIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(api.getWebSocketUrl());

    ws.onopen = () => {
      console.log("WebSocket connected");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.error) {
        console.error("WebSocket error:", data.error);
        setIsProcessing(false);
        return;
      }

      if (data.text !== undefined && data.is_final) {
        const startTime = Date.now();

        // Add user message (voice)
        const userMessage: Message = {
          id: Date.now().toString(),
          role: "user",
          content: data.text,
          timestamp: new Date(),
          isVoice: true,
        };

        // Add assistant response
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.llm_response || "I received your message but couldn't process it.",
          timestamp: new Date(),
          responseTime: Date.now() - startTime,
        };

        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setIsProcessing(false);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsProcessing(false);
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
    };

    wsRef.current = ws;
  }, [chatId]);

  // Send audio to WebSocket
  const sendAudio = useCallback(async () => {
    if (audioChunksRef.current.length === 0) return;

    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    audioChunksRef.current = [];

    // Prepare chat history for context
    const history = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Send start command with language and history
    wsRef.current?.send(
      JSON.stringify({
        action: "start",
        language: language,
        history: history,
      })
    );

    await new Promise((resolve) => setTimeout(resolve, 100));

    // Send audio blob
    const arrayBuffer = await audioBlob.arrayBuffer();
    wsRef.current?.send(arrayBuffer);

    await new Promise((resolve) => setTimeout(resolve, 100));
    wsRef.current?.send(JSON.stringify({ action: "stop" }));
  }, [language, messages]);

  // Stop recording
  const doStopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    setIsRecording(false);

    if (checkSilenceIntervalRef.current) {
      clearInterval(checkSilenceIntervalRef.current);
      checkSilenceIntervalRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    silenceStartRef.current = null;
    setIsProcessing(true);
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      connectWebSocket();

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("Connection timeout")), 5000);
        const checkConnection = () => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            clearTimeout(timeout);
            resolve();
          } else if (wsRef.current?.readyState === WebSocket.CLOSED) {
            clearTimeout(timeout);
            reject(new Error("WebSocket connection failed"));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4",
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        sendAudio();
      };

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      mediaRecorder.start(100);
      isRecordingRef.current = true;
      setIsRecording(true);
      silenceStartRef.current = null;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      checkSilenceIntervalRef.current = window.setInterval(() => {
        if (!isRecordingRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalizedLevel = avg / 255;

        if (normalizedLevel < SILENCE_THRESHOLD) {
          if (silenceStartRef.current === null) {
            silenceStartRef.current = Date.now();
          } else if (Date.now() - silenceStartRef.current > SILENCE_DURATION_MS) {
            console.log("Silence detected, stopping");
            doStopRecording();
          }
        } else {
          silenceStartRef.current = null;
        }
      }, 100);
    } catch (error) {
      console.error("Failed to start recording:", error);
      setIsRecording(false);
    }
  }, [connectWebSocket, doStopRecording, sendAudio]);

  // Send text message
  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: content.trim(),
      timestamp: new Date(),
      isVoice: false,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const startTime = Date.now();

    try {
      const history = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const response = await fetch(`${api.baseUrl}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: content.trim(),
          history: history,
        }),
      });

      if (!response.ok) throw new Error(await response.text());

      const data: ChatResponse = await response.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: data.content || "I apologize, I received an empty response.",
        timestamp: new Date(),
        responseTime: Date.now() - startTime,
      };

      // Use functional update to include userMessage that was just added
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : "Unknown error";
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: `I'm having trouble connecting. Please try again.\n\nError: ${errMsg}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Chat management
  const handleNewChat = () => {
    setMessages([]);
    setChatId(null);
  };

  const handleSelectChat = (id: string) => {
    const chat = chatHistoryService.getChat(id);
    if (chat) {
      setMessages(chat.messages);
      setChatId(id);
    }
  };

  const handleDeleteChat = (id: string) => {
    chatHistoryService.deleteChat(id);
    setChats(chatHistoryService.getAllChats());
    if (chatId === id) {
      setMessages([]);
      setChatId(null);
    }
  };

  // Show company name in header if selected
  const activeCompanyName = companies.find((c) => c.id === activeCompanyId)?.name;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Company Selector Modal */}
      {showCompanySelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-2 text-center" style={{ color: "#0b015d" }}>
              اختر الشركة
            </h2>
            <p className="text-sm text-gray-500 mb-4 text-center">
              Select the company you want to work with
            </p>
            <div className="space-y-2">
              {companies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelectCompany(company.id)}
                  className="w-full px-4 py-3 text-right border-2 rounded-xl transition-all hover:shadow-md flex items-center justify-between"
                  style={{ borderColor: "#d3a035", color: "#0b015d" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#d3a035";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.color = "#0b015d";
                  }}
                >
                  <span className="text-xs opacity-60">ID: {company.id}</span>
                  <span className="font-medium">{company.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <Sidebar
        chats={chats}
        currentChatId={chatId}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        isOpen={sidebarOpen}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1 flex items-center gap-3">
            <Image
              src="/mdarjLogoAR.svg"
              alt="MDARJ"
              width={80}
              height={40}
              className="h-8 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold" style={{ color: "#0b015d" }}>
                Voice Assistant
              </h1>
              <p className="text-xs text-gray-500">Powered by AI</p>
            </div>
          </div>
          {/* Company indicator */}
          {activeCompanyName && (
            <button
              onClick={() => companies.length > 1 && setShowCompanySelector(true)}
              className="px-3 py-1.5 text-sm rounded-lg border transition-all hover:shadow-sm flex items-center gap-2"
              style={{ borderColor: "#d3a035", color: "#0b015d" }}
              title={companies.length > 1 ? "Click to switch company" : "Active company"}
            >
              <span style={{ color: "#d3a035" }}>&#9679;</span>
              {activeCompanyName}
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Image
                src="/mdarjLogoAR.svg"
                alt="MDARJ"
                width={200}
                height={100}
                className="mb-6"
              />
              <h2 className="text-2xl font-bold mb-2" style={{ color: "#0b015d" }}>
                مرحباً بك في مساعد MDARJ الصوتي
              </h2>
              <p className="text-gray-500 max-w-md mb-6">
                مساعدك الذكي لنظام Odoo. اطرح أسئلتك حول بيانات عملك باستخدام الصوت أو الكتابة بالعربية أو الإنجليزية.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  "ما هو إجمالي المبيعات هذا الشهر؟",
                  "أظهر لي أفضل 5 عملاء",
                  "كم عدد الفواتير غير المدفوعة؟",
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(suggestion)}
                    className="px-4 py-2 bg-white border-2 rounded-full text-sm transition-all hover:shadow-md"
                    style={{
                      borderColor: "#d3a035",
                      color: "#0b015d",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#d3a035";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.color = "#0b015d";
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <MessageList messages={messages} isTyping={isLoading || isProcessing} />
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <ChatInput
          onSendMessage={sendMessage}
          onStartRecording={startRecording}
          onStopRecording={doStopRecording}
          isLoading={isLoading}
          isRecording={isRecording}
          isProcessing={isProcessing}
          language={language}
          onLanguageChange={setLanguage}
        />
      </div>
    </div>
  );
}
